import { useState, ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { APP_VERSION, BUILD_DATE, COPYRIGHT_YEAR, COMPANY_NAME } from '../../../config/version';

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Load from localStorage if available
    const saved = localStorage.getItem('intelligenceSidebarCollapsed');
    return saved === 'true';
  });
  const location = useLocation();

  // Auto-collapse sidebar on chat page for more space
  useEffect(() => {
    if (location.pathname === '/intelligence/chat') {
      setSidebarCollapsed(true);
    }
  }, [location.pathname]);

  // Save collapsed state to localStorage
  const handleToggleCollapse = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('intelligenceSidebarCollapsed', String(newState));
  };

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-900 flex flex-col overflow-hidden">
      <Header onMenuClick={() => setSidebarOpen(true)} />
      <div className="flex flex-1 min-h-0">
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={handleToggleCollapse}
        />
        {/* NOTE: Chat.tsx escapes this p-4 with negative margins and a
            calc(100vh-120px) height — keep the padding and the h-12 app bar /
            footer heights in sync with that constant. */}
        <main className="flex-1 overflow-auto p-4">
          {children}
        </main>
      </div>
      <footer className="flex-shrink-0 bg-white border-t border-gray-200 py-2 px-6 dark:bg-gray-800 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row items-center justify-between text-xs text-gray-500 gap-1 dark:text-gray-400">
          <p>&copy; {COPYRIGHT_YEAR} {COMPANY_NAME}. All rights reserved.</p>
          <p>Version {APP_VERSION} &middot; {BUILD_DATE}</p>
        </div>
      </footer>
    </div>
  );
}
